import { DemoClientPresentation } from '@innerhive-internal/innerhive-common';
import { DemoClientPhotoCatalog } from './DemoClientPhotoCatalog';

/**
 * The photos the invented caseload wears: stock portraits bundled with the app, dealt out afresh
 * each time the demo is turned on or its caseload is read again, so no two demos show the same
 * faces in the same places and nobody learns to read a face as a particular client.
 */
export class DemoClientPhotos {
    static inject = () => [DemoClientPhotoCatalog];

    constructor(private catalog: DemoClientPhotoCatalog) {}

    /**
     * One photo per client, in the clients' order, each a face that agrees with the client's
     * presentation, in a random order and, within a presentation, each used once before any is used twice. A client with no
     * presentation, or none the catalogue has photos for, takes one of whatever is left. Empty for an
     * empty catalogue, rather than dealing for ever.
     */
    deal = ({ presentations, random = Math.random }: { presentations: (DemoClientPresentation | undefined)[]; random?: () => number }): string[] => {
        const { photos } = this.catalog;
        if (photos.length === 0) {
            return [];
        }
        const filesPresenting = (presents: DemoClientPresentation) => photos.filter(photo => photo.presents === presents).map(photo => photo.file);
        const pools: Record<DemoClientPresentation, string[]> = { woman: filesPresenting('woman'), man: filesPresenting('man') };
        const decks: Record<DemoClientPresentation, string[]> = { woman: [], man: [] };
        const used = new Set<string>();

        const dealt = presentations.map(presents => {
            if (!presents || pools[presents].length === 0) {
                return undefined;
            }
            const file = this.draw({ deck: decks[presents], refill: pools[presents], random });
            used.add(file);
            return file;
        });

        const allFiles = photos.map(photo => photo.file);
        const rest = this.shuffle(allFiles.filter(file => !used.has(file)), random);
        return dealt.map(file => file ?? this.draw({ deck: rest, refill: allFiles, random }));
    };

    /** The deck's next photo, shuffling the whole refill into it once it runs out. The refill is never empty. */
    private draw = ({ deck, refill, random }: { deck: string[]; refill: string[]; random: () => number }): string => {
        if (deck.length === 0) {
            deck.push(...this.shuffle(refill, random));
        }
        const [next] = deck.splice(0, 1);
        return next;
    };

    private shuffle = (files: string[], random: () => number): string[] => {
        const shuffled = [...files];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };
}
